import React, { useState, useEffect } from 'react';
import { useForm, usePage, Head } from '@inertiajs/react';
import { TextInput, Textarea, Button, Box, Text, MultiSelect, Paper, MantineProvider } from '@mantine/core';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import FormLayout from '../../components/FormLayout'; // Provides common layout/styling
import ImageUploadModal from '../../components/ImageUploadModal'; // Modal for image uploads

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

// Interface for the data within the form
interface UserFormData {
  username: string;
  email: string;
  bio: string;
  avatar: File | null;
  post_seo_image: File | null;
  role_ids: string[];
}

// Interface for props passed via Inertia from the controller
interface EditUserProps {
  user: {
    id: number;
    username: string;
    email: string;
    bio?: string;
    avatar_url?: string | null;
    post_seo_image_url?: string | null;
    // Additional associations if needed
    role_ids: string[];
    event_ids: string[];
    artist_ids: string[];
    organizer_ids: string[];
    private_lesson_feature_flag: boolean;
  };
  available_roles: Array<{ value: string; label: string }>;
  all_events: Array<{ value: string; label: string }>;
  all_artists: Array<{ value: string; label: string }>;
  all_organizers: Array<{ value: string; label: string }>;
  current_user_roles: string[];
  can_delete: boolean;
  update_url: string;
  delete_url: string;
  errors?: Partial<Record<keyof UserFormData | string, string>>;
  onSuccess?: () => void;
}

export default function EditUser({ user, update_url, available_roles, onSuccess }: EditUserProps) {
  // Use Inertia's useForm hook for form state management
  const { data, setData, put, processing, errors, recentlySuccessful } = useForm<UserFormData>({
    username: user.username,
    email: user.email,
    bio: user.bio || '',
    avatar: null,
    post_seo_image: null,
    role_ids: user?.role_ids && Array.isArray(user.role_ids) ? user.role_ids.map((id) => id.toString()) : [],
  });

  // State for preview images
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user.avatar_url ?? null);
  const [postSeoPreview, setPostSeoPreview] = useState<string | null>(user.post_seo_image_url ?? null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Update preview if the form submission is successful or if prop image URLs change
  useEffect(() => {
    console.log('EditUser props:', {
      user,
      'user.role_ids': user?.role_ids,
      'user.avatar_url': user?.avatar_url,
      update_url,
      available_roles,
    });
    console.log('🤓🤓🤓🤓User avatar URL:', user.avatar_url);
    console.log('🛠 update_url:', update_url); // Add this debug log
    if (recentlySuccessful) {
      setAvatarPreview(user.avatar_url ?? null);
      setPostSeoPreview(user.post_seo_image_url ?? null);
    }
  }, [user.avatar_url, user.post_seo_image_url, recentlySuccessful]);

  // Handlers to update file inputs and previews
  const handleAvatarChange = (file: File) => {
    setData('avatar', file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handlePostSeoChange = (file: File) => {
    setData('post_seo_image', file);
    setPostSeoPreview(URL.createObjectURL(file));
  };

  // inside your EditUser component…

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('Form submission started');

    // Create form data object that matches Rails expectations
    const formData = new FormData();

    // Add _method=PUT to tell Rails this is a PUT request
    formData.append('_method', 'PUT');

    // Use the correct parameter structure for Rails
    formData.append('user[username]', data.username);
    formData.append('user[email]', data.email);
    formData.append('user[bio]', data.bio);

    // Handle role_ids as an array
    if (data.role_ids && data.role_ids.length > 0) {
      data.role_ids.forEach((roleId) => {
        formData.append('user[role_ids][]', roleId);
      });
    }

    // Add file uploads if they exist
    if (data.avatar) formData.append('user[avatar]', data.avatar);
    if (data.post_seo_image) formData.append('user[post_seo_image]', data.post_seo_image);

    // Use fetch directly to handle the submission
    fetch(update_url, {
      method: 'POST', // Still use POST but include _method=PUT
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        'X-Inertia': 'true',
        Accept: 'text/html, application/xhtml+xml',
      },
      body: formData,
    })
      .then((response) => {
        if (response.ok) {
          // Success - show notification
          showNotification({
            id: 'saving-changes',
            color: 'green',
            title: 'Profile Updated',
            message: 'Your changes were saved successfully.',
            icon: <IconCheck />,
            autoClose: 3000,
            position: 'top-right',
          });

          setShowSuccess(true);

          // Fetch the updated user data from the server
          return fetch(`/users/${data.username}/settings_data.json`)
            .then((response) => {
              if (!response.ok) {
                throw new Error('Failed to fetch updated user data');
              }
              return response.json();
            })
            .then((updatedData) => {
              console.log('Fetched updated user data:', updatedData);

              // Close the drawer with updated data
              if (typeof onSuccess === 'function') {
                console.log('Calling onSuccess with updated data');
                onSuccess(updatedData);
              }

              setTimeout(() => {
                setShowSuccess(false);
              }, 1000);
            });
        } else {
          // Error - show notification
          showNotification({
            id: 'saving-changes',
            color: 'red',
            title: 'Update Failed',
            message: 'Please check the form for errors and try again.',
            icon: <IconAlertCircle />,
            autoClose: 5000,
          });

          return Promise.reject('Form submission failed');
        }
      })
      .catch((error) => {
        console.error('Error submitting form:', error);
        showNotification({
          id: 'saving-changes',
          color: 'red',
          title: 'Update Failed',
          message: 'An error occurred while saving your changes.',
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
      });
  };

  return (
    <>
      <Notifications />
      {showSuccess && (
        <Box
          style={{
            textAlign: 'center',
            marginBottom: '1rem',
          }}
        >
          <IconCheck size={48} color="green" />
          <Text fw={500} mt="sm" c="green.6">
            Profile updated!
          </Text>
        </Box>
      )}
      <Paper title="Edit Your User Account" style={{ backgroundColor: '#171717' }}>
        <Head title={`Edit ${user.username}`} />

        <form onSubmit={handleSubmit}>
          {/* Avatar image upload */}
          <ImageUploadModal previewUrl={avatarPreview} aspectRatio={1 / 1} onImageChange={handleAvatarChange} />
          {errors.avatar && (
            <Text c="red" size="sm" mt={-10} mb={10}>
              {errors.avatar}
            </Text>
          )}

          <TextInput
            label="Username"
            value={data.username}
            onChange={(e) => setData('username', e.target.value)}
            mt="md"
            required
            error={errors.username}
            disabled={processing}
          />

          <TextInput
            label="Email"
            value={data.email}
            onChange={(e) => setData('email', e.target.value)}
            mt="md"
            required
            error={errors.email}
            disabled={processing}
          />

          <Textarea
            label="Bio"
            value={data.bio}
            onChange={(e) => setData('bio', e.target.value)}
            my="lg"
            error={errors.bio}
            disabled={processing}
          />

          <MultiSelect
            data={available_roles}
            label="Roles"
            searchable
            hidePickedOptions
            placeholder={data.role_ids.length === 0 ? 'Select roles' : ''}
            value={data.role_ids}
            onChange={(value) => setData('role_ids', value)}
            mt="md"
            error={errors.role_ids}
          />

          {errors.post_seo_image && (
            <Text c="red" size="sm" mt={-10} mb={10}>
              {errors.post_seo_image}
            </Text>
          )}

          <Button type="submit" mt="xl" loading={processing}>
            Update User
          </Button>
        </form>
      </Paper>
    </>
  );
}
